// src/theme/blaynaTheme.js
import { createTheme, alpha } from "@mui/material/styles";

export const theme = createTheme({
  palette: {
    mode: "light",

    background: {
      default: "#F3F5FB",
      paper: "#FFFFFF",
    },

    primary: {
      main: "#2563EB", // main brand blue
      light: "#93C5FD", // soft hover/active blue
      dark: "#1E3A8A", // navy shadow color
      contrastText: "#FFFFFF",
    },

    secondary: {
      main: "#1E3A8A",
      light: "#93C5FD",
      contrastText: "#FFFFFF",
    },

    success: { main: "#2E7D32" },
    warning: { main: "#FB8C00" },
    error: { main: "#D32F2F" },

    text: {
      primary: "#1E242C",
      secondary: "#5B6472",
    },

    divider: "rgba(30,41,59,0.08)",
  },

  shape: { borderRadius: 12 },

  typography: {
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',

    h4: { fontWeight: 800, color: "#1E242C" },
    h5: { fontWeight: 800, color: "#1E242C" },
    h6: { fontWeight: 700, color: "#1E242C" },

    subtitle1: { color: "#38404B", fontWeight: 600 },

    caption: { color: "#5B6472", fontWeight: 500 },

    body1: { color: "#1E242C", fontWeight: 500 },
    body2: { color: "#38404B" },

    button: { textTransform: "none", fontWeight: 700, letterSpacing: 0.2 },
  },

  components: {
    // --------------------------
    // Global
    // --------------------------
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background:
            "linear-gradient(180deg, #FFFFFF 0%, #EBF1FF 30%, #F5F6FA 100%)",
          color: "#1E242C",
        },
      },
    },

    // --------------------------
    // Paper / Cards
    // --------------------------
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
          border: "1px solid rgba(30,41,59,0.06)",
          boxShadow:
            "0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.05)",
          borderRadius: 12,
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
          border: "1px solid rgba(30,41,59,0.06)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          borderRadius: 16,
        },
      },
    },

    // --------------------------
    // Buttons
    // --------------------------
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 700,
          "&:hover": {
            boxShadow: "0 2px 6px rgba(30,41,59,0.15)",
          },
          "&:active": {
            transform: "scale(0.98)",
          },
          "&:focus-visible": {
            outline: "2px solid #BFDBFE",
            outlineOffset: 2,
          },
        },

        containedPrimary: {
          backgroundColor: "#2563EB",
          "&:hover": { backgroundColor: "#1D4ED8" },
        },

        outlinedPrimary: {
          borderColor: "#2563EB",
          "&:hover": {
            backgroundColor: "#EFF6FF",
            borderColor: "#1D4ED8",
          },
        },
      },
    },

    // --------------------------
    // Chips
    // --------------------------
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 600,
        },
        filledPrimary: {
          backgroundColor: "#2563EB",
          "&:hover": { backgroundColor: "#1D4ED8" },
        },
        outlinedPrimary: {
          borderColor: "#2563EB",
          "&:hover": {
            backgroundColor: "#EFF6FF",
            borderColor: "#1D4ED8",
          },
        },
      },
    },

    // --------------------------
    // Inputs / TextFields
    // --------------------------
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "& fieldset": {
            borderColor: "rgba(30,41,59,0.2)",
          },
          "&:hover fieldset": {
            borderColor: "#93C5FD",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#2563EB",
            boxShadow: "0 0 0 3px rgba(147,197,253,0.4)", // soft glow
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.Mui-focused": {
            color: "#2563EB",
          },
        },
      },
    },

    // --------------------------
    // Navigation / Sidebar
    // --------------------------
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          "&.Mui-selected": {
            backgroundColor: "#EFF6FF",
            color: "#1E3A8A",
            "&:hover": {
              backgroundColor: "#DBEAFE", // stronger light blue
            },
          },
          "&:hover": {
            backgroundColor: "#F0F6FF",
          },
        },
      },
    },

    // --------------------------
    // Dividers
    // --------------------------
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: "rgba(30,41,59,0.08)" },
      },
    },

    // --------------------------
    // Tooltip
    // --------------------------
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#1E242C",
          color: "#FFFFFF",
          fontSize: "0.8rem",
          borderRadius: 6,
        },
      },
    },
  },
});
